import { type MCPServerInfo } from '../../types';

const CnaGwreDeveloperAssistantServer: MCPServerInfo = {
  serverId: 'CNA_GWRE_DEVELOPER_ASSISTANT',
  name: 'Guidewire Developer Assistant',
  slug: 'cna-gwre-developer-assistant',
  description:
    'Semantic search over Guidewire Integration Gateway and Insurance Suite documentation. Runs locally and connects to the Guidewire Developer Assistant API.',
  authenticationType: 'STATIC_CREDS',
  source: {
    sourceType: 'LOCAL_PACKAGE',
    transport: 'STDIO',
    // @gwmcp/is-developer-assistant is distributed through Guidewire's private npm
    // registry — npx -y would attempt to fetch from the public npmjs.com registry
    // and fail for users whose npm is not configured for it.
    //
    commandFunction: `(config) => ({
  command: 'is-developer-assistant',
  args: [],
  env: {
    // Trust the OS certificate store
    NODE_OPTIONS: '--use-system-ca',
    // Natoma passes the dev key through GW_MCP_JWT_TOKEN, a slot originally designed for
    // Okta JWT tokens. This works because the SDK treats both identically — each is sent
    // as Authorization: Bearer <token> in API requests.
    ...(config.devkey ? { GW_MCP_JWT_TOKEN: config.devkey } : {}),
    ...(config.devassistantServiceUrl ? { DEVASSISTANT_SERVICE_URL: config.devassistantServiceUrl } : {}),
    ...(config.oktaClientId ? { OKTA_CLIENT_ID: config.oktaClientId } : {}),
    ...(config.oktaDomain ? { OKTA_DOMAIN: config.oktaDomain } : {}),
    ...(config.devassistantSocketFile ? { DEVASSISTANT_SOCKET_FILE: config.devassistantSocketFile } : {}),
  }
})`
  },
  serverConfig: {
    required: [],
    properties: {}
  },
  installConfig: {
    required: [],
    properties: {
      devkey: {
        type: 'string',
        description:
          'Dev key from Guidewire (Home → App Tools → Developer Assistant → "Show MCP Key"). Valid for 7 days.',
        sensitive: true
      },
      devassistantServiceUrl: {
        type: 'string',
        description:
          'DEVASSISTANT_SERVICE_URL — Developer Assistant API base URL.',
        sensitive: false,
        default: 'https://devassistant.api.omega2-andromeda.guidewire.net'
      },
      oktaClientId: {
        type: 'string',
        description:
          'OKTA_CLIENT_ID — Required for Okta browser sign-in; not needed if a dev key is provided.',
        sensitive: false
      },
      oktaDomain: {
        type: 'string',
        description:
          'OKTA_DOMAIN — Required for Okta browser sign-in; not needed if a dev key is provided. e.g. https://guidewire-hub.okta.com/oauth2/<id>.',
        sensitive: false
      },
      devassistantSocketFile: {
        type: 'string',
        description:
          'DEVASSISTANT_SOCKET_FILE — Unix socket path for hook integration. When set, logs user prompts to the Developer Assistant service.',
        sensitive: false,
        default: '/tmp/devassistant-hook.tmp'
      }
    }
  },
  instructions: {
    installSetupInstructions: `## Requirements

1. You are a member of the \`gwa.any_planet_class.codelift.user\` group in your IdP (required to use Developer Assistant MCP)
2. You are a member of the \`{tenant}.all.all.all.binrepo.read\` group in your IdP (required to access the BinRepo package registry)

## Instructions

### 1. Install

Login to BinRepo (replace \`{tenant}\` with your Guidewire tenant):

\`\`\`bash
npm login --registry=https://amer.binrepo.guidewire.net/artifactory/api/npm/mcp-{tenant}-all-npm-virtual/ --auth-type=web --scope=@gwmcp
\`\`\`

Install the MCP server:

\`\`\`bash
npm install -g @gwmcp/is-developer-assistant@latest
\`\`\`

### 2. Authenticate

There are two ways to authenticate. Choose one:

**Option A — Devkey via Natoma (recommended)**

1. Go to **Guidewire Home > App Tools > Developer Assistant** and click **Show MCP Key** to get your devkey.
2. Paste it into the **Devkey** field in the Natoma install form.

Natoma stores the devkey securely and uses it to authenticate on your behalf — no local files needed.

**Option B — Local file-based auth (fallback)**

1. Run \`is-developer-assistant login\` in a terminal.
2. Choose either "MCP Developer Key" (valid 7 days) or "Okta browser-based authentication" (valid 12 hours).
3. Credentials are saved to \`~/.gwmcp/is-devassistant-auth.json\`.
4. Leave the **Devkey** field in Natoma blank — the server will read credentials from the local file automatically.

### 3. After connecting

One connection serves all your Guidewire projects. In each project directory, run \`is-developer-assistant setup\` to generate a \`CLAUDE.md\` or \`AGENTS.md\` file — this tells your AI assistant which GW collections to query for that project.
`
  }
};

export default CnaGwreDeveloperAssistantServer;
